import { get, post, patch } from './client';
import type {
  CloudinaryAssetsResponse,
  CloudinaryFolder,
  CloudinaryFolderTreeResponse,
  CloudinaryStatus,
  CloudinaryStats,
  CloudinaryFilters,
} from '@/types/api';

// Credentials management
export interface CloudinaryCredentials {
  cloud_name?: string;
  api_key?: string;
  api_secret?: string;
  enable_sync?: boolean;
  clear_credentials?: boolean;
}

export interface CloudinaryCredentialsResponse {
  cloudinary_configured: boolean;
  cloudinary_sync_enabled: boolean;
  cloudinary_cloud_name: string | null;
  cloudinary_last_synced_at: string | null;
}

export async function updateCloudinaryCredentials(
  teamId: string,
  credentials: CloudinaryCredentials
): Promise<CloudinaryCredentialsResponse> {
  return post(`/api/teams/${teamId}/cloudinary/credentials`, credentials);
}

export async function testCloudinaryConnection(
  teamId: string
): Promise<{ status: string; cloud_name: string; usage: Record<string, unknown> }> {
  return get(`/api/teams/${teamId}/cloudinary/test`);
}

// Sync
export interface CloudinarySyncResponse {
  status: 'complete' | 'scheduled';
  assets_fetched?: number;
  assets_added?: number;
  assets_updated?: number;
  errors?: number;
  message?: string;
  last_synced_at: string | null;
}

export async function triggerCloudinarySync(
  teamId: string,
  options?: { maxPages?: number; resume?: boolean; wait?: boolean }
): Promise<CloudinarySyncResponse> {
  const params = new URLSearchParams();
  if (options?.maxPages !== undefined) {
    params.set('max_pages', options.maxPages.toString());
  }
  if (options?.resume !== undefined) {
    params.set('resume', options.resume.toString());
  }
  if (options?.wait !== undefined) {
    params.set('wait', options.wait.toString());
  }
  const query = params.toString();
  return post(`/api/teams/${teamId}/cloudinary/sync${query ? `?${query}` : ''}`);
}

// Assets
export async function getCloudinaryAssets(
  teamId: string,
  filters?: CloudinaryFilters,
  pagination?: { limit?: number; offset?: number }
): Promise<CloudinaryAssetsResponse> {
  const params = new URLSearchParams();

  if (pagination?.limit) params.set('limit', pagination.limit.toString());
  if (pagination?.offset) params.set('offset', pagination.offset.toString());
  if (filters && filters.folder !== undefined) params.set('folder', filters.folder);
  if (filters?.folder_prefix) params.set('folder_prefix', filters.folder_prefix);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.sort_by) params.set('sort_by', filters.sort_by);
  if (filters?.sort_order) params.set('sort_order', filters.sort_order);

  const query = params.toString();
  return get(`/api/teams/${teamId}/cloudinary/assets${query ? `?${query}` : ''}`);
}

export async function getCloudinaryFolders(
  teamId: string,
  format?: 'flat' | 'tree'
): Promise<{ folders: CloudinaryFolder[] }> {
  const params = format ? `?format=${format}` : '';
  return get(`/api/teams/${teamId}/cloudinary/assets/folders${params}`);
}

export async function getCloudinaryFolderTree(
  teamId: string
): Promise<CloudinaryFolderTreeResponse> {
  return get(`/api/teams/${teamId}/cloudinary/assets/folders?format=tree`);
}

export async function getCloudinaryStatuses(
  teamId: string
): Promise<{ statuses: CloudinaryStatus[] }> {
  return get(`/api/teams/${teamId}/cloudinary/assets/statuses`);
}

export async function updateCloudinaryAsset(
  teamId: string,
  assetId: string,
  update: { status?: string; notes?: string }
): Promise<{
  id: string;
  public_id: string;
  status: string | null;
  status_timestamp: string | null;
  notes: string | null;
}> {
  return patch(`/api/teams/${teamId}/cloudinary/assets/${assetId}`, update);
}

// Stats
export async function getCloudinaryStats(teamId: string): Promise<CloudinaryStats> {
  return get(`/api/teams/${teamId}/cloudinary/stats`);
}
